import React, { useEffect, useState } from "react";
import Divider from "components/common/Divider";
import ChevronLeftIcon from "assets/icons/ChevronLeftIcon";
import ChevronRightIcon from "assets/icons/ChevronRightIcon";
import { useAppSelector } from "store/hooks";
import { getSettings } from "store/slices";
import { t } from "i18next";
import Loader from "components/Loader";
import { addUserToWatchList, getSubjects, useAddUserAnswer } from "api/academy";
import { ApiResponse } from "constantVariables";
import { useLifeChoicePageContext } from "context/hooks/useLifeChoicePageContext";
import useUser from "context/hooks/user";
import { decrypt } from "utils/crypto";

export default function QuestionSection({ selectedCourse }) {
  const { dir, language } = useAppSelector(getSettings);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [questions, setQuestions] = useState({
    isLoading: true,
    data: [],
  });
  const { mutate: addUserAnswer } = useAddUserAnswer();
  const { setLifeChoice } = useLifeChoicePageContext();
  const { data: user } = useUser();

  useEffect(() => {
    if (selectedCourse.subjects) {
      addUserToWatchList({ courseId: selectedCourse._id });
      setLifeChoice((prev) => ({
        ...prev,
        academy: prev.academy.map((ele) => ({
          ...ele,
          courses: ele.courses.map((course) => ({
            ...course,
            started_quiz: course.started_quiz
              ? course.started_quiz
              : course._id === selectedCourse._id,
          })),
        })),
      }));

      getSubjects({ subjects: selectedCourse.subjects })
        .then((res) => {
          if (res.statusCode === ApiResponse.SUCCESS) {
            setQuestions({ isLoading: false, data: decrypt(res.data) });
          }
        })
        .catch(() => {
          setQuestions({ isLoading: false, data: [] });
        });
    }
  }, []);

  useEffect(() => {
    if (questions.data[currentQuestion]?.users_selected_option?.length > 0) {
      const optionId = questions.data[currentQuestion].users_selected_option[0].option;
      setSelectedOption(
        questions.data[currentQuestion].options.find((option) => option.id === optionId)
      );
    }
  }, [questions.data[currentQuestion]]);

  return (
    <>
      <div dir={dir}>
        <p className="mt-4 text-base">
          {questions.data[currentQuestion]?.subject?.description?.[language]}
        </p>
      </div>
      <Divider className="my-5" />
      {questions.isLoading ? (
        <div className="relative">
          <Loader open type="block" />
        </div>
      ) : questions.data.length ? (
        <>
          <h4 className="font-semibold" dir={dir}>
            Q. {questions.data[currentQuestion].question_text[language]}
          </h4>
          <div className="flex flex-col gap-3 my-4" dir={dir}>
            {questions.data[currentQuestion].options.map((option, index) => (
              <div
                className={`flex items-center gap-2 border border-theme-gray cursor-pointer rounded-full px-6 py-2 ${
                  selectedOption
                    ? selectedOption?.id ===
                        questions.data[currentQuestion].correct_answer &&
                      selectedOption?.id === option.id
                      ? "border-[#17B14A] bg-[#17B14A] text-white"
                      : selectedOption?.id === option.id
                      ? "border-[#D84646] bg-[#D84646] text-white"
                      : ""
                    : ""
                }`}
                key={index}
                onClick={() => {
                  // if (selectedOption?.id) return;
                  setSelectedOption(option);
                  addUserAnswer({
                    id: questions.data[currentQuestion]._id,
                    option: option.id,
                  });
                  setQuestions((prev) => ({
                    ...prev,
                    data: prev.data.map((ele, idx) =>
                      idx === currentQuestion
                        ? {
                            ...ele,
                            users_selected_option: [
                              {
                                option: option.id,
                                user: user._id,
                              },
                            ],
                          }
                        : ele
                    ),
                  }));
                }}
              >
                <p>{option[language]}</p>
              </div>
            ))}
            <div className="flex items-center justify-between my-4">
              <div
                className={`flex items-center gap-1
          ${currentQuestion === 0 ? "cursor-not-allowed" : "cursor-pointer"}
          `}
                onClick={() => {
                  if (currentQuestion !== 0) {
                    setCurrentQuestion((prev) => prev - 1);
                    setSelectedOption(null);
                  }
                }}
              >
                <ChevronLeftIcon
                  fill={currentQuestion === 0 ? "#BACFE3" : "#004E99"}
                  className={`${dir === "rtl" ? "rotate-180" : ""}`}
                />
                <p
                  className={`${
                    currentQuestion === 0 ? "text-[#BACFE3]" : "text-theme-blue"
                  }`}
                >
                  {t("questionNavigationLeft")}
                </p>
              </div>
              <p>
                {currentQuestion + 1}/{questions.data.length}
              </p>
              <div
                className={`flex items-center gap-1 ${
                  !selectedOption &&
                  currentQuestion + 1 !== questions.data.length
                    ? "cursor-pointer"
                    : "cursor-not-allowed"
                }`}
                onClick={() => {
                  if (
                    selectedOption &&
                    currentQuestion + 1 !== questions.data.length
                  ) {
                    setCurrentQuestion((prev) => prev + 1);
                    setSelectedOption(null);
                  }
                }}
              >
                <p
                  className={`${
                    selectedOption &&
                    currentQuestion + 1 !== questions.data.length
                      ? "text-theme-blue"
                      : "text-[#BACFE3]"
                  }`}
                >
                  {t("questionNavigationRight")}
                </p>
                <ChevronRightIcon
                  fill={
                    selectedOption &&
                    currentQuestion + 1 !== questions.data.length
                      ? "#004E99"
                      : "#BACFE3"
                  }
                  className={`${dir === "rtl" ? "rotate-180" : ""}`}
                />
              </div>
            </div>
            {selectedOption?.explanation && (
              <div>
                <p className=" font-light">
                  <span className="text-red-500">* </span>
                  {selectedOption?.explanation[language]}
                </p>
              </div>
            )}
          </div>
        </>
      ) : (
        <p>No questions found!</p>
      )}
    </>
  );
}
